<template>
  <div class="min-h-screen bg-slate-50 flex flex-col justify-center py-12 sm:px-6 lg:px-8">
    <Head :title="`Actualización de Datos - ${account?.reasonName || 'Condominio'}`" />

    <div class="sm:mx-auto sm:w-full sm:max-w-xl">
      <!-- Logo Header -->
      <div class="flex justify-center mb-6">
        <logo :src="logo" class="h-14 w-auto object-contain" />
      </div>
      <h2 class="text-center text-3xl font-extrabold text-gray-900">
        Actualización de Datos de Propietarios
      </h2>
      <p class="mt-2 text-center text-sm text-gray-600 max-w-md mx-auto">
        Campaña de actualización de datos para el envío digital de recibos de pago y avisos de cobro por correo electrónico.
      </p>
    </div>

    <div class="mt-8 sm:mx-auto sm:w-full sm:max-w-xl px-4">
      <div class="bg-white py-8 px-8 shadow-md border border-slate-200 rounded-xl">
        
        <!-- Step 1: Search / Select Owner if not selected yet -->
        <div v-if="!selectedClient && !successMessage">
          <div class="mb-6 p-4 rounded-lg bg-indigo-50 border border-indigo-100 flex items-start">
            <svg class="h-5 w-5 text-indigo-600 mr-2.5 flex-shrink-0 mt-0.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            <div class="text-xs text-indigo-800 leading-relaxed">
              <strong>Paso 1:</strong> Ingresa tu <strong>Cédula de Identidad</strong>, <strong>Nro. de Parcela / Casa</strong> o <strong>Nombre</strong> para buscar tu ficha en el sistema.
            </div>
          </div>

          <form @submit.prevent="searchClient" class="space-y-4">
            <div>
              <label class="block text-sm font-semibold text-gray-700">Identificación, Parcela o Cédula</label>
              <div class="mt-1 flex rounded-md shadow-sm">
                <input
                  type="text"
                  v-model="searchQuery"
                  class="focus:ring-indigo-500 focus:border-indigo-500 flex-1 block w-full rounded-none rounded-l-md sm:text-sm border-gray-300 px-3 py-2"
                  placeholder="Ej: V-12345678, Parcela A-2, etc."
                  required
                />
                <button
                  type="submit"
                  :disabled="searching"
                  class="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-r-md text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
                >
                  <svg v-if="searching" class="animate-spin -ml-1 mr-2 h-4 w-4 text-white" fill="none" viewBox="0 0 24 24">
                    <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
                    <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                  Buscar
                </button>
              </div>
              <p v-if="searchError" class="text-red-600 text-xs mt-1.5 font-medium">{{ searchError }}</p>
            </div>
          </form>

          <!-- Search Results List -->
          <div v-if="searchResults.length > 0" class="mt-6 space-y-2 border-t pt-4">
            <p class="text-xs font-semibold text-gray-500 uppercase tracking-wider">Selecciona tu inmueble:</p>
            <div
              v-for="item in searchResults"
              :key="item.id"
              @click="selectClient(item)"
              class="p-3 border rounded-lg hover:border-indigo-500 hover:bg-indigo-50/50 cursor-pointer transition-all flex items-center justify-between"
            >
              <div>
                <p class="font-bold text-gray-900 text-sm">{{ item.name }}</p>
                <p class="text-xs text-gray-500">Parcela / Inmueble: {{ item.identification || item.companyName || 'N/A' }} <span v-if="item.street">| Calle: {{ item.street.name }}</span></p>
              </div>
              <span class="text-xs font-semibold text-indigo-600 bg-indigo-100 px-2.5 py-1 rounded-md">Seleccionar &rarr;</span>
            </div>
          </div>
        </div>

        <!-- Step 2: Update Form -->
        <div v-if="selectedClient && !successMessage">
          <div class="mb-6 flex items-center justify-between pb-3 border-b border-gray-200">
            <div>
              <span class="text-xs font-bold text-indigo-600 uppercase">Actualizando datos de:</span>
              <h3 class="text-lg font-extrabold text-gray-900">{{ selectedClient.name }}</h3>
              <p class="text-xs text-gray-500">Inmueble: {{ selectedClient.identification }}</p>
            </div>
            <button
              type="button"
              @click="selectedClient = null"
              class="text-xs font-medium text-gray-500 hover:text-gray-700 underline"
            >
              Cambiar
            </button>
          </div>

          <form @submit.prevent="submitUpdate" class="space-y-4">
            <div>
              <label class="block text-sm font-semibold text-gray-700">
                Correo Electrónico <span class="text-red-500">*</span>
              </label>
              <p class="text-xs text-gray-500 mb-1">Aquí se te enviarán tus recibos y avisos de cobro oficiales.</p>
              <input
                type="email"
                v-model="form.email"
                required
                class="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
                placeholder="ejemplo@correo.com"
              />
              <p v-if="form.errors.email" class="text-red-500 text-xs mt-1">{{ form.errors.email }}</p>
            </div>

            <div>
              <label class="block text-sm font-semibold text-gray-700">
                Teléfono / WhatsApp <span class="text-red-500">*</span>
              </label>
              <input
                type="text"
                v-model="form.phone"
                required
                class="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
                placeholder="0412-1234567"
              />
              <p v-if="form.errors.phone" class="text-red-500 text-xs mt-1">{{ form.errors.phone }}</p>
            </div>

            <!-- Propietario / Titular (Solo Lectura) -->
            <div class="bg-slate-50 border border-slate-200 rounded-lg p-3.5">
              <div class="flex items-center justify-between">
                <span class="text-xs font-bold text-gray-500 uppercase tracking-wide">Titular Registrado</span>
                <span class="inline-flex items-center text-[11px] font-semibold text-gray-600 bg-gray-200/80 px-2 py-0.5 rounded">
                  <svg class="w-3 h-3 mr-1 text-gray-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                  </svg>
                  Solo lectura
                </span>
              </div>
              <p class="text-base font-extrabold text-gray-900 mt-1">{{ selectedClient.name }}</p>
            </div>

            <!-- Dirección de Vivienda (Solo Lectura) -->
            <div class="bg-slate-50 border border-slate-200 rounded-lg p-3.5">
              <div class="flex items-center justify-between">
                <span class="text-xs font-bold text-gray-500 uppercase tracking-wide">Dirección de Vivienda / Referencia</span>
                <span class="inline-flex items-center text-[11px] font-semibold text-gray-600 bg-gray-200/80 px-2 py-0.5 rounded">
                  <svg class="w-3 h-3 mr-1 text-gray-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                  </svg>
                  Solo lectura
                </span>
              </div>
              <p class="text-base font-semibold text-gray-900 mt-1">{{ selectedClient.address || 'No registrada' }}</p>
            </div>

            <div class="pt-4 flex items-center justify-between">
              <button
                type="button"
                @click="selectedClient = null"
                class="px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 bg-white hover:bg-gray-50"
              >
                Atrás
              </button>
              <button
                type="submit"
                :disabled="form.processing"
                class="inline-flex justify-center items-center py-2.5 px-6 border border-transparent rounded-md shadow-sm text-sm font-bold text-white bg-green-600 hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500 transition-colors"
              >
                <svg v-if="form.processing" class="animate-spin -ml-1 mr-2 h-4 w-4 text-white" fill="none" viewBox="0 0 24 24">
                  <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
                  <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
                Guardar y Actualizar Datos
              </button>
            </div>
          </form>
        </div>

        <!-- Success Message -->
        <div v-if="successMessage" class="text-center py-6">
          <div class="mx-auto flex items-center justify-center h-16 w-16 rounded-full bg-green-100 mb-4">
            <svg class="h-10 w-10 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 13l4 4L19 7" />
            </svg>
          </div>
          <h3 class="text-2xl font-black text-gray-900 mb-2">¡Datos Actualizados con Éxito!</h3>
          <p class="text-sm text-gray-600 max-w-md mx-auto mb-6">
            Hemos registrado tu correo electrónico <strong>{{ lastEmail }}</strong>. A partir de ahora recibirás todos los recibos de pago y avisos de cobro directamente en tu bandeja de entrada.
          </p>
          <div class="space-y-3">
            <a
              v-if="lastClientId"
              :href="`/payments/${lastClientId}`"
              class="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-bold text-white bg-indigo-600 hover:bg-indigo-700"
            >
              Ver Mi Recibo / Reportar Pago &rarr;
            </a>
            <div>
              <button
                type="button"
                @click="resetAll"
                class="text-xs font-semibold text-gray-500 hover:text-gray-800 underline"
              >
                Actualizar otro inmueble
              </button>
            </div>
          </div>
        </div>

      </div>

      <p class="mt-6 text-center text-xs text-gray-500 max-w-sm mx-auto">
        Sistema de Gestión de Condominios. Todos los derechos reservados.
      </p>
    </div>
  </div>
</template>

<script>
import { Head } from '@inertiajs/inertia-vue3'
import Logo from '@/Shared/Logo'
import axios from 'axios'

export default {
  components: {
    Head,
    Logo,
  },
  props: {
    account: Object,
    logo: String,
    initialClient: Object,
  },
  data() {
    return {
      searchQuery: '',
      searching: false,
      searchError: null,
      searchResults: [],
      selectedClient: this.initialClient || null,
      successMessage: false,
      lastEmail: '',
      lastClientId: null,
      form: this.$inertia.form({
        id: this.initialClient ? this.initialClient.id : null,
        email: this.initialClient ? this.initialClient.email : '',
        phone: this.initialClient ? this.initialClient.phone : '',
      }),
    }
  },
  methods: {
    async searchClient() {
      if (!this.searchQuery.trim()) return
      this.searching = true
      this.searchError = null
      this.searchResults = []

      try {
        const response = await axios.post('/actualizar-datos/buscar', { query: this.searchQuery })
        if (response.data.clients && response.data.clients.length > 0) {
          if (response.data.clients.length === 1) {
            this.selectClient(response.data.clients[0])
          } else {
            this.searchResults = response.data.clients
          }
        } else {
          this.searchError = 'No se encontró ningún propietario con los datos ingresados. Verifica el número de parcela o cédula.'
        }
      } catch (err) {
        this.searchError = 'Ocurrió un error al buscar. Por favor intenta de nuevo.'
      } finally {
        this.searching = false
      }
    },
    selectClient(client) {
      this.selectedClient = client
      this.form.id = client.id
      this.form.email = client.email || ''
      this.form.phone = client.phone || ''
      this.searchResults = []
    },
    submitUpdate() {
      this.form.post('/actualizar-datos', {
        preserveScroll: true,
        onSuccess: () => {
          this.lastEmail = this.form.email
          this.lastClientId = this.selectedClient?.id || this.form.id
          this.successMessage = true
        },
      })
    },
    resetAll() {
      this.searchQuery = ''
      this.selectedClient = null
      this.successMessage = false
      this.searchResults = []
    },
  },
}
</script>
